import { useCallback, useLayoutEffect, useMemo, useRef } from 'react'

import { useLayoutEditorContext } from 'features/views/LayoutEditor/useLayoutEditorContext'
import { useLayoutEditorWidgetAreaContext } from 'features/views/LayoutEditor/useLayoutEditorWidgetAreaContext'
import { TabsWidgetType } from 'features/views/LayoutEditor/widgets/tabsWidgetTypes'

import { useTabsWidgetTabs } from './useTabsWidgetTabs'

export function useTabsWidgetState(widget: TabsWidgetType) {
    const { urlParams } = useLayoutEditorContext()
    const urlParamsRef = useRef(urlParams)
    urlParamsRef.current = urlParams

    const { visibleTabs } = useTabsWidgetTabs(widget)

    const widgetUrlSlug = widget.attrs.urlSlug || widget.id

    const setSelectedTabUrlSlug = useCallback(
        (tabUrlSlug?: string) => {
            urlParamsRef.current.setParam(widgetUrlSlug, tabUrlSlug, true)
        },
        [widgetUrlSlug]
    )

    const firstTabUrlSlug = visibleTabs[0]?.urlSlug || visibleTabs[0]?.id
    const selectedTabUrlSlug = urlParams.values[widgetUrlSlug] || firstTabUrlSlug

    const isSelectedTabVisible = !!visibleTabs.find((tab) => {
        const urlSlug = tab.urlSlug || tab.id

        return urlSlug === selectedTabUrlSlug
    })

    useLayoutEffect(() => {
        const update = setTimeout(() => {
            // If the selected tab does not exist anymore, select the first tab.
            if (!isSelectedTabVisible) {
                setSelectedTabUrlSlug(firstTabUrlSlug)
            }
        }, 500)

        return () => {
            clearTimeout(update)
        }
    }, [setSelectedTabUrlSlug, isSelectedTabVisible, firstTabUrlSlug])

    const { children } = useLayoutEditorWidgetAreaContext()!
    const isFirstChild = children[0]?.id === widget.id

    return useMemo(
        () => ({
            selectedTabUrlSlug,
            setSelectedTabUrlSlug,
            visibleTabs,
            isFirstChild,
        }),
        [selectedTabUrlSlug, setSelectedTabUrlSlug, visibleTabs, isFirstChild]
    )
}
